import type { QuizPublic } from '@repo/types/quiz';

import { useGetQuizzesInfinite } from '@repo/api/quiz/useGetQuizzesInfinite';
import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { AwardIcon, CalendarIcon, CheckIcon, RepeatIcon, TimerIcon, TimerOffIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { SearchInput } from '@/components/ui/input';
import { SelectList, SelectListElement, SelectListElementContent, SelectListElementSkeleton, SelectListElementTitle, SelectListInfiniteQueryStates } from '@/components/ui/select-list';

import { AssignmentBuilderQuizCard, QuizTypeBadge } from './AssignmentBuilderQuizCard';
import { formatQuizDeadline, formatQuizMaxAttempts, formatQuizPoints, formatQuizTime } from './quizStatFormatters';

export interface AssignmentBuilderQuizDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedQuiz: QuizPublic | null;
  onSelect: (quiz: QuizPublic | null) => void;
}

export default function AssignmentBuilderQuizDialog(props: AssignmentBuilderQuizDialogProps) {
  const { open, onOpenChange, selectedQuiz, onSelect } = props;

  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue: string = useDebounceValue(searchValue, 400);

  const getQuizzes = useGetQuizzesInfinite(searchDebouncedValue || undefined);

  const { data, isEmpty, noResults } = useInfiniteQueryResult(getQuizzes.data, searchDebouncedValue);

  const handlePick = (quiz: QuizPublic) => {
    onSelect(quiz);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:h-[85dvh] sm:min-h-96 sm:max-w-120">
        <DialogBody>
          <DialogHeader className="border-border-primary bg-card border-b">
            <DialogTitle>Wybierz quiz</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">Wybierz quiz do załączenia do zadania</DialogDescription>
          <div className="flex grow flex-col gap-3 overflow-y-auto px-3 py-3 md:px-4">
            <SearchInput setValue={setSearchValue} placeholder="Wyszukaj..." />
            <SelectList
              type="single"
              selected={selectedQuiz ? [selectedQuiz.id] : []}
              setSelected={(ids) => {
                const picked = ids[0] ? (data.find((q) => q.id === ids[0]) ?? null) : null;
                onSelect(picked);
                onOpenChange(false);
              }}
            >
              <SelectListInfiniteQueryStates query={getQuizzes} isEmpty={isEmpty} noResults={noResults} isEmptyMessage="Brak dostępnych quizów" noResultMessage="Nie znaleziono quizu" />

              {data.map((quiz) => (
                <QuizDialogRow key={quiz.id} quiz={quiz} isSelected={quiz.id === selectedQuiz?.id} onPick={() => handlePick(quiz)} />
              ))}

              <InfiniteScroll hasNextPage={getQuizzes.hasNextPage} isLoading={getQuizzes.isLoading} isFetching={getQuizzes.isFetching} next={getQuizzes.fetchNextPage} threshold={0.65}>
                {getQuizzes.hasNextPage && <SelectListElementSkeleton />}
              </InfiniteScroll>
            </SelectList>
          </div>
        </DialogBody>
        <DialogFooter className="border-border-primary bg-accent dark:bg-card border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full md:w-fit">
            Zamknij
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface QuizDialogRowProps {
  quiz: QuizPublic;
  isSelected: boolean;
  onPick: () => void;
}

function QuizDialogRow(quizDialogRowProps: QuizDialogRowProps) {
  const { quiz, isSelected, onPick } = quizDialogRowProps;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <SelectListElement id={quiz.id} className="items-start">
          <SelectListElementContent>
            <div className="flex items-center gap-2">
              <SelectListElementTitle className="font-medium">{quiz.name}</SelectListElementTitle>
              <QuizTypeBadge type={quiz.type} />
            </div>
            <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <RowStat icon={<TimerIcon className="size-3.5" />} label={formatQuizTime(quiz.time)} />
              <RowStat icon={<AwardIcon className="size-3.5" />} label={formatQuizPoints(quiz.points, { withUnit: true })} />
              <RowStat
                icon={<RepeatIcon className="size-3.5" />}
                label={formatQuizMaxAttempts(quiz.maxAttempts, {
                  withUnit: true,
                })}
              />
              <RowStat icon={quiz.deadline ? <CalendarIcon className="size-3.5" /> : <TimerOffIcon className="size-3.5" />} label={formatQuizDeadline(quiz.deadline)} />
            </div>
          </SelectListElementContent>
          <Button
            type="button"
            size="sm"
            variant={isSelected ? 'primary' : 'outline'}
            onClick={(event) => {
              event.stopPropagation();
              onPick();
            }}
            className="shrink-0 self-center"
          >
            {isSelected ? (
              <>
                <CheckIcon />
                Wybrany
              </>
            ) : (
              'Wybierz'
            )}
          </Button>
        </SelectListElement>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-80 overflow-hidden p-0" collisionPadding={16}>
        <AssignmentBuilderQuizCard quiz={quiz} className="rounded-none border-0 bg-transparent" />
      </HoverCardContent>
    </HoverCard>
  );
}

interface RowStatProps {
  icon: React.ReactNode;
  label: string;
}

function RowStat(rowStatProps: RowStatProps) {
  const { icon, label } = rowStatProps;
  return (
    <span className="inline-flex items-center gap-1">
      {icon}
      {label}
    </span>
  );
}
